/**
 * Exact Figma icons for the CVs list + manage sheets (file O1alcAoKpuBRziK0TWF1eM,
 * nodes 3062-6333 / 3147-1394 / 3147-1474 / 3147-1554 / 3147-2039). Each glyph is
 * the literal Subtract/Vector path exported from Figma: NOT a lucide approximation
 * This keeps the row icons, badge mark, and create-chooser glyphs aligned 1:1.
 *
 * All accept `size` + `color`; stroke icons keep the Figma 1.5 / 2 stroke weights
 * and round caps. The Tabbio "Main" mark reuses the shared TabbioMark.
 */
import Svg, { Path } from 'react-native-svg';

type IconProps = { size?: number; color?: string };

const STROKE = {
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** Action sheet → "Open" (arrow-right). */
export function CvOpenIcon({ size = 18, color = '#7D7C81' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path d="M15.25 9H2.75" stroke={color} {...STROKE} />
      <Path d="M11 13.25L15.25 9L11 4.75" stroke={color} {...STROKE} />
    </Svg>
  );
}

/** Action sheet → "Rename" (pencil). */
export function CvRenameIcon({ size = 18, color = '#7D7C81' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path
        d="M2.75 15.25C2.75 15.25 6.34901 14.682 7.29601 13.735C8.24301 12.788 14.623 6.40799 14.623 6.40799C15.46 5.57099 15.46 4.21399 14.623 3.37799C13.786 2.54099 12.429 2.54099 11.593 3.37799C11.593 3.37799 5.21301 9.75799 4.26601 10.705C3.31901 11.652 2.75101 15.251 2.75101 15.251L2.75 15.25Z"
        stroke={color}
        {...STROKE}
      />
      <Path d="M10 15.25H15.25" stroke={color} {...STROKE} />
    </Svg>
  );
}

/** Action sheet → "Make New From This" (duplicate, filled). viewBox 16×17. */
export function CvDuplicateIcon({ size = 18, color = '#7D7C81' }: IconProps) {
  return (
    <Svg width={size} height={(size * 17) / 16} viewBox="0 0 16 17" fill="none">
      <Path
        d="M4.5625 2.6311H9.40625C9.48368 2.63105 9.56623 2.62974 9.64941 2.63013L9.89648 2.63794C10.1272 2.65368 10.3549 2.69962 10.5732 2.77563H10.5742C11.2281 3.00513 11.7637 3.48587 12.0635 4.1106C12.2003 4.39571 12.2833 4.70356 12.3086 5.0188L12.3193 5.30298V6.41528L12.3184 9.07056V12.22L12.3193 13.2268C12.3197 13.6958 12.3409 14.0761 12.1729 14.5549C11.9413 15.2127 11.4569 15.7505 10.8271 16.05L10.8281 16.051C10.6697 16.1269 10.5148 16.1827 10.3438 16.2258C10.3018 16.2364 10.2224 16.2493 10.1562 16.261C10.1214 16.2671 10.0886 16.2737 10.0615 16.2795C10.048 16.2825 10.0361 16.2849 10.0273 16.2874C10.019 16.2897 10.0153 16.291 10.0146 16.2913L9.99512 16.3H3.48438L3.46777 16.2932L3.29297 16.2581C3.18367 16.2385 3.07767 16.2172 2.96582 16.1809C2.28709 15.9637 1.72603 15.4789 1.41211 14.8391C1.27319 14.5558 1.18751 14.2491 1.16016 13.9348V13.9338C1.14526 13.7543 1.14942 13.55 1.14941 13.3772L1.15039 12.5715V6.74634L1.14941 5.71411C1.14928 5.52619 1.14383 5.2868 1.1543 5.09497C1.16585 4.85581 1.21104 4.61953 1.28809 4.39282V4.39185C1.51205 3.73552 1.98894 3.19572 2.6123 2.89185C2.89023 2.75726 3.18971 2.6725 3.49707 2.64282H3.49805C3.79328 2.61593 4.26458 2.63099 4.5625 2.6311ZM9.10547 4.08325L7.76367 4.08423L5.11523 4.08325C4.67085 4.08324 4.18617 4.07246 3.75195 4.08423C3.43979 4.1004 3.19516 4.18092 2.98047 4.3772L2.88965 4.46899C2.77508 4.59718 2.69202 4.74936 2.64551 4.91333L2.61133 5.0813C2.60336 5.14427 2.60046 5.22052 2.59961 5.29907V6.20435L2.60059 8.50317V12.0315L2.59961 13.1516C2.59939 13.3388 2.59221 13.5853 2.60254 13.759V13.76C2.61611 14.032 2.72679 14.2894 2.91211 14.4856L2.99609 14.5657L2.99707 14.5667C3.15418 14.7051 3.34715 14.7962 3.55371 14.8303H3.55273C3.74234 14.8599 4.09904 14.8501 4.32227 14.8499H8.2998C8.77093 14.8499 9.2593 14.8564 9.72559 14.8479L9.91113 14.8303C9.96923 14.8214 10.0256 14.8078 10.085 14.7874C10.4955 14.6453 10.8089 14.2717 10.8564 13.8411C10.8643 13.7695 10.8681 13.6972 10.8691 13.6233V10.3831L10.8682 6.91138L10.8691 5.80591C10.8698 5.20828 10.924 4.7635 10.4746 4.36841C10.0779 4.01978 9.66154 4.08288 9.10547 4.08325Z"
        fill={color}
      />
      <Path
        d="M12.4824 0.100342L12.5 0.106201C12.517 0.112413 12.5473 0.118954 12.5859 0.125732C12.6043 0.128948 12.6232 0.132588 12.6416 0.135498L12.6934 0.143311L12.835 0.173584C12.8815 0.184773 12.9278 0.196748 12.9736 0.210693H12.9746C13.6374 0.414737 14.1919 0.87378 14.5176 1.48608H14.5186C14.6843 1.79968 14.7831 2.14418 14.8105 2.4978L14.8203 2.73804C14.821 2.81854 14.8203 2.89838 14.8203 2.97339L14.8193 3.62183L14.8203 5.71655L14.8193 10.366V11.4656L14.8232 11.9119C14.8224 11.9983 14.8192 12.0854 14.8135 12.1619C14.8079 12.2361 14.7989 12.3082 14.7822 12.3611V12.3621C14.7216 12.5475 14.5887 12.7008 14.4141 12.7878C14.2423 12.8729 14.0438 12.8867 13.8623 12.825C13.5345 12.7134 13.3617 12.4236 13.3701 12.0984C13.3744 11.9356 13.3691 11.7693 13.3691 11.6003L13.3701 10.4509V2.85327C13.3713 2.59595 13.3555 2.40557 13.2471 2.18823C13.1053 1.90411 12.8521 1.6914 12.5479 1.60034C12.4388 1.56819 12.2593 1.55444 12.0615 1.55054C11.8652 1.54667 11.6678 1.55242 11.5215 1.55249H5.71484C5.63722 1.55251 5.50885 1.55665 5.39062 1.55444C5.27345 1.55225 5.14298 1.54392 5.05273 1.51343C4.86653 1.4513 4.71274 1.31696 4.62695 1.14038V1.1394C4.46852 0.808174 4.59131 0.377528 4.92188 0.192139C4.95634 0.172843 4.99299 0.159105 5.02246 0.148193C5.05423 0.136432 5.07803 0.12764 5.09961 0.116943L5.1123 0.111084L5.13379 0.100342H12.4824Z"
        fill={color}
      />
    </Svg>
  );
}

/** Action sheet → "Version History" (clock). */
export function CvClockIcon({ size = 18, color = '#7D7C81' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path
        d="M9 16.25C13.0041 16.25 16.25 13.0041 16.25 9C16.25 4.99594 13.0041 1.75 9 1.75C4.99594 1.75 1.75 4.99594 1.75 9C1.75 13.0041 4.99594 16.25 9 16.25Z"
        stroke={color}
        {...STROKE}
      />
      <Path d="M9 4.75V9L12.25 11.25" stroke={color} {...STROKE} />
    </Svg>
  );
}

/** Action sheet → "Archive" (trash). Destructive: Figma red #FF3034. */
export function CvTrashIcon({ size = 18, color = '#FF3034' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path
        d="M13.6971 7.75L13.3494 14.35C13.2934 15.4201 12.4154 16.25 11.3524 16.25H6.64743C5.58343 16.25 4.70642 15.42 4.65042 14.35L4.30273 7.75"
        stroke={color}
        {...STROKE}
      />
      <Path d="M2.75 4.75H15.25" stroke={color} {...STROKE} />
      <Path
        d="M6.75 4.75V2.75C6.75 2.2 7.198 1.75 7.75 1.75H10.25C10.802 1.75 11.25 2.2 11.25 2.75V4.75"
        stroke={color}
        {...STROKE}
      />
    </Svg>
  );
}

/** Create chooser → "For me" (single person). Brand-tinted glyph. */
export function CvForMeIcon({ size = 18, color = '#5A2AFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path
        d="M9 7.25C10.5188 7.25 11.75 6.01878 11.75 4.5C11.75 2.98122 10.5188 1.75 9 1.75C7.48122 1.75 6.25 2.98122 6.25 4.5C6.25 6.01878 7.48122 7.25 9 7.25Z"
        stroke={color}
        {...STROKE}
      />
      <Path
        d="M13.7624 15.516C14.6224 15.245 15.0744 14.295 14.7094 13.471C13.7394 11.28 11.5504 9.75 9.00035 9.75C6.45035 9.75 4.26135 11.28 3.29135 13.471C2.92635 14.296 3.37835 15.245 4.23835 15.516C5.46335 15.902 7.08435 16.25 9.00035 16.25C10.9164 16.25 12.5374 15.902 13.7624 15.516Z"
        stroke={color}
        {...STROKE}
      />
    </Svg>
  );
}

/** Create chooser → "For someone else" (two people). Brand-tinted glyph. */
export function CvForOthersIcon({ size = 18, color = '#5A2AFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path
        d="M11.0408 14.8175C11.8778 14.5265 12.3068 13.5605 11.9068 12.7695C11.0008 10.9785 9.1458 9.74951 7.0008 9.74951C4.8558 9.74951 3.00079 10.9775 2.09479 12.7695C1.69479 13.5605 2.12279 14.5265 2.96079 14.8175C3.99179 15.1755 5.3688 15.5005 7.0008 15.5005C8.6328 15.5005 10.0098 15.1755 11.0408 14.8175Z"
        stroke={color}
        {...STROKE}
      />
      <Path
        d="M12.4767 6.2485C12.4848 6.2485 12.4922 6.25049 12.5003 6.25049C13.7433 6.25049 14.7503 5.24339 14.7503 4.00049C14.7503 2.75759 13.7433 1.75049 12.5003 1.75049C12.3534 1.75049 12.2124 1.77199 12.0742 1.80099"
        stroke={color}
        {...STROKE}
      />
      <Path
        d="M7 7.25049C8.519 7.25049 9.75 6.01949 9.75 4.50049C9.75 2.98149 8.519 1.75049 7 1.75049C5.481 1.75049 4.25 2.98149 4.25 4.50049C4.25 6.01949 5.481 7.25049 7 7.25049Z"
        stroke={color}
        {...STROKE}
      />
      <Path
        d="M15.0389 13.4355C15.4228 13.3413 15.7847 13.2382 16.096 13.1274C16.798 12.8774 17.106 12.0505 16.771 11.3845C15.985 9.82149 14.369 8.74951 12.5 8.74951"
        stroke={color}
        {...STROKE}
      />
    </Svg>
  );
}

/** Header "+" (plus). Default white: sits on the brand circle. */
export function CvPlusIcon({ size = 18, color = '#FFFFFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path d="M9 3.25V14.75" stroke={color} {...STROKE} />
      <Path d="M3.25 9H14.75" stroke={color} {...STROKE} />
    </Svg>
  );
}

/** Search field glyph. viewBox 24, 2px stroke, Figma secondary #8E939F. */
export function CvSearchIcon({ size = 24, color = '#8E939F' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <Path
        d="M16.9265 17.04L20.3996 20.4M19.2796 11.44C19.2796 15.7699 15.7695 19.28 11.4396 19.28C7.1097 19.28 3.59961 15.7699 3.59961 11.44C3.59961 7.11006 7.1097 3.59998 11.4396 3.59998C15.7695 3.59998 19.2796 7.11006 19.2796 11.44Z"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Card overflow "⋮" (vertical three-dot). Figma muted #949399 (fill + stroke). */
export function CvMoreIcon({ size = 18, color = '#949399' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 18 18" fill="none">
      <Path
        d="M9 9.5C9.27614 9.5 9.5 9.27614 9.5 9C9.5 8.72386 9.27614 8.5 9 8.5C8.72386 8.5 8.5 8.72386 8.5 9C8.5 9.27614 8.72386 9.5 9 9.5Z"
        fill={color}
        stroke={color}
        {...STROKE}
      />
      <Path
        d="M9 3.75C9.27614 3.75 9.5 3.52614 9.5 3.25C9.5 2.97386 9.27614 2.75 9 2.75C8.72386 2.75 8.5 2.97386 8.5 3.25C8.5 3.52614 8.72386 3.75 9 3.75Z"
        fill={color}
        stroke={color}
        {...STROKE}
      />
      <Path
        d="M9 15.25C9.27614 15.25 9.5 15.0261 9.5 14.75C9.5 14.4739 9.27614 14.25 9 14.25C8.72386 14.25 8.5 14.4739 8.5 14.75C8.5 15.0261 8.72386 15.25 9 15.25Z"
        fill={color}
        stroke={color}
        {...STROKE}
      />
    </Svg>
  );
}

/**
 * Owner-accordion chevron. Base glyph points end-ward (right in LTR). viewBox 12.
 * Callers rotate 90° when expanded (points down) and mirror for RTL.
 */
export function CvChevronIcon({ size = 12, color = '#949399' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <Path d="M4.25 1.75L8.5 6L4.25 10.25" stroke={color} {...STROKE} />
    </Svg>
  );
}
